import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, Pressable, StyleSheet, Text, View, type PressableStateCallbackType } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaDietWash } from '../../styles/theme';
import type { QuizDeckEntry } from '../../data/onboardingDeck';
import { deckTitle } from '../../services/onboardingQuiz';
import type { Axis } from '../../services/tasteAxes';

/**
 * - idle: waiting for a tap.
 * - win / lose: the OUT phase right after a tap (1500 ms). The winner holds,
 *   lifts, then flies up into the pile while the simmer medallion confirms
 *   the choice; the loser drops away.
 * - arrive: the IN phase (560 ms), the next pair is dealt in from the right.
 * - chosen: static, the pick shown again after browser back from safety.
 */
export type DuelCardState = 'idle' | 'win' | 'lose' | 'arrive' | 'chosen';

interface Props {
  entry: QuizDeckEntry;
  state: DuelCardState;
  onPress: () => void;
  testID: string;
  lang: 'fr' | 'en';
  /** Phone stacks the two cards; desktop puts them side by side. */
  layout: 'stack' | 'side';
  /** 0 for the left/top card, 1 for the other; the second one is dealt 120 ms later. */
  index: 0 | 1;
  /**
   * The axis this duel tests. A card shows the photo, the title and the one
   * fact on that axis, nothing else: minutes, ratings, publisher, difficulty
   * and cost all differed between the two plates and turned a question about
   * protein versus vegetal into a question about Ricardo versus IGA.
   */
  axis: Axis;
}

/**
 * Timings and curves lifted from the design (dc-onb.css: dFly, dDrop, dDeal,
 * potIn, potOut). The quiz screen's OUT_MS (1500) and IN_MS (560) are the
 * SQuiz timers those keyframes were cut for.
 */
export const DUEL_MOTION = {
  fly: { duration: 1500, easing: Easing.bezier(0.35, 0.05, 0.4, 1) },
  drop: { delay: 500, duration: 500, easing: Easing.bezier(0.42, 0, 1, 1) },
  deal: { duration: 500, stagger: 120, easing: Easing.bezier(0.22, 0.9, 0.3, 1) },
  pot: { inDuration: 340, inEasing: Easing.bezier(0.3, 1.2, 0.5, 1), outAt: 450, outDuration: 1000, outEasing: Easing.bezier(0.42, 0, 1, 1) },
} as const;

const POT_SIZE = 150;
/** The pan in the simmer medallion (decision 2026-09-17: the pan, not the plate's photo). */
const PAN = require('../../../assets/onboarding/pan.png');

export function DuelCard({ entry, state, onPress, testID, lang, layout, index, axis }: Props) {
  const { t } = useTranslation();
  // One driver per keyframe set; each is interpolated below.
  const fly = useRef(new Animated.Value(0)).current;
  const drop = useRef(new Animated.Value(0)).current;
  const deal = useRef(new Animated.Value(1)).current;
  const pot = useRef(new Animated.Value(0)).current;
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const running: Animated.CompositeAnimation[] = [];
    if (state === 'win') {
      fly.setValue(0); drop.setValue(0); deal.setValue(1); pot.setValue(0);
      running.push(Animated.timing(fly, { toValue: 1, duration: DUEL_MOTION.fly.duration, easing: DUEL_MOTION.fly.easing, useNativeDriver: false }));
      running.push(Animated.sequence([
        Animated.timing(pot, { toValue: 1, duration: DUEL_MOTION.pot.inDuration, easing: DUEL_MOTION.pot.inEasing, useNativeDriver: false }),
        Animated.delay(DUEL_MOTION.pot.outAt - DUEL_MOTION.pot.inDuration),
        Animated.timing(pot, { toValue: 2, duration: DUEL_MOTION.pot.outDuration, easing: DUEL_MOTION.pot.outEasing, useNativeDriver: false }),
      ]));
    } else if (state === 'lose') {
      fly.setValue(0); drop.setValue(0); deal.setValue(1);
      running.push(Animated.sequence([
        Animated.delay(DUEL_MOTION.drop.delay),
        Animated.timing(drop, { toValue: 1, duration: DUEL_MOTION.drop.duration, easing: DUEL_MOTION.drop.easing, useNativeDriver: false }),
      ]));
    } else if (state === 'arrive') {
      fly.setValue(0); drop.setValue(0); pot.setValue(0); deal.setValue(0);
      running.push(Animated.sequence([
        Animated.delay(index * DUEL_MOTION.deal.stagger),
        Animated.timing(deal, { toValue: 1, duration: DUEL_MOTION.deal.duration, easing: DUEL_MOTION.deal.easing, useNativeDriver: false }),
      ]));
    } else {
      fly.setValue(0); drop.setValue(0); pot.setValue(0); deal.setValue(1);
    }
    running.forEach((a) => a.start());
    return () => running.forEach((a) => a.stop());
  }, [state, index, fly, drop, deal, pot]);

  const title = deckTitle(entry, lang);
  const dot = PaprikaDietWash[entry.dietKind].dot;

  // dFly: hold until 34 %, lift at 55 %, then shrink into the pile above.
  const flyY = fly.interpolate({ inputRange: [0, 0.34, 0.55, 1], outputRange: [0, 0, -16, -0.52 * Math.max(height, 1)] });
  const flyScale = fly.interpolate({ inputRange: [0, 0.34, 0.55, 1], outputRange: [1, 1, 1.02, 0.1] });
  const flyRotate = fly.interpolate({ inputRange: [0, 0.34, 0.55, 1], outputRange: ['0deg', '0deg', '-1.5deg', '-9deg'] });
  const flyOpacity = fly.interpolate({ inputRange: [0, 0.55, 1], outputRange: [1, 1, 0.2] });
  // dDrop: the loser falls 46 px, tilts 3°, fades out.
  const dropY = drop.interpolate({ inputRange: [0, 1], outputRange: [0, 46] });
  const dropRotate = drop.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '3deg'] });
  const dropOpacity = drop.interpolate({ inputRange: [0, 1], outputRange: [1, 0] });
  // dDeal: the next card slides in from 46 px right, tilted 4°, from 96 %.
  const dealX = deal.interpolate({ inputRange: [0, 1], outputRange: [46, 0] });
  const dealRotate = deal.interpolate({ inputRange: [0, 1], outputRange: ['4deg', '0deg'] });
  const dealScale = deal.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1] });
  // potIn then potOut: 0 → 1 pops the medallion, 1 → 2 lets it go.
  const potOpacity = pot.interpolate({ inputRange: [0, 1, 2], outputRange: [0, 1, 0] });
  const potScale = pot.interpolate({ inputRange: [0, 1, 2], outputRange: [0.82, 1, 1.06] });

  const motion = {
    opacity: Animated.multiply(Animated.multiply(flyOpacity, dropOpacity), deal),
    transform: [
      { translateY: Animated.add(flyY, dropY) },
      { translateX: dealX },
      { scale: Animated.multiply(flyScale, dealScale) },
      { rotate: state === 'win' ? flyRotate : state === 'lose' ? dropRotate : dealRotate },
    ],
    zIndex: state === 'win' ? 4 : 1,
  };

  return (
    <Animated.View
      style={[styles.wrap, layout === 'side' ? styles.side : styles.stack, motion]}
      onLayout={(e) => setHeight(e.nativeEvent.layout.height)}
    >
      <Pressable
        onPress={onPress}
        disabled={state !== 'idle'}
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={title}
        style={({ hovered }: PressableStateCallbackType & { hovered?: boolean }) => [styles.card, hovered && state === 'idle' && styles.cardHover]}
      >
        {/* Explicit 100% sizes: on web a bundled asset brings its own
            width/height as default style, and that height beats the
            absolute-fill edges once the card is taller than the photo. */}
        <Image source={entry.image} style={[StyleSheet.absoluteFill, styles.photo]} resizeMode="cover" />
        <LinearGradient
          colors={['rgba(27,18,11,0)', 'rgba(27,18,11,0.5)', 'rgba(27,18,11,0.88)']}
          locations={[0, 0.58, 1]}
          style={styles.scrim}
        />
        <View style={styles.caption}>
          <Text style={[styles.title, layout === 'stack' && styles.titleStack]} numberOfLines={2}>{title}</Text>
          <View style={styles.meta} testID={`${testID}-fact`}>
            {axis === 'base' && (
              <>
                <View style={[styles.dot, { backgroundColor: dot }]} />
                <Text style={styles.metaText}>{t(`onboarding.quiz.side.${entry.dietKind === 'veg' ? 'vegetal' : 'protein'}`)}</Text>
              </>
            )}
            {axis === 'time' && (
              <>
                <Feather name="clock" size={12} color="rgba(255,255,255,0.92)" />
                <Text style={styles.metaText}>{t('onboarding.quiz.minutes', { count: entry.minutes })}</Text>
              </>
            )}
            {axis === 'heat' && (
              <Text style={styles.metaText}>{t(`onboarding.quiz.side.${entry.intensity}`)}</Text>
            )}
          </View>
        </View>
        {state === 'win' && (
          <View style={styles.simmer} pointerEvents="none" testID={`${testID}-win`}>
            <Animated.View style={[styles.pot, { opacity: potOpacity, transform: [{ scale: potScale }] }]}>
              {/* The pan on the stove: one drawing for every winner, ringed so
                  it reads as its own object over the photo behind it. */}
              <View style={styles.potInner}>
                <Image source={PAN} style={styles.potPan} resizeMode="contain" />
              </View>
            </Animated.View>
          </View>
        )}
        {state === 'chosen' && (
          <View style={styles.chosenBadge} testID={`${testID}-chosen`}>
            <Feather name="check-circle" size={28} color={Paprika.accent} />
          </View>
        )}
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrap: { minHeight: 0 },
  stack: { flex: 1, minHeight: 200 },
  side: { flex: 1, minHeight: 360, maxHeight: 640 },
  card: {
    flex: 1,
    borderRadius: 18,
    overflow: 'hidden',
    backgroundColor: Paprika.surfaceLow,
    boxShadow: '0 14px 34px -16px rgba(82, 68, 60, 0.42)',
  },
  cardHover: { transform: [{ translateY: -3 }] },
  photo: { width: '100%', height: '100%' },
  scrim: { position: 'absolute', left: 0, right: 0, bottom: 0, height: '62%' },
  caption: { position: 'absolute', left: 0, right: 0, bottom: 0, paddingHorizontal: 18, paddingTop: 18, paddingBottom: 20, gap: 8 },
  title: { fontFamily: 'Newsreader-Regular', fontSize: 24, lineHeight: 27, color: '#ffffff' },
  titleStack: { fontSize: 20, lineHeight: 23 },
  meta: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  metaText: { fontFamily: 'Inter-Medium', fontSize: 12, color: 'rgba(255,255,255,0.92)' },
  dot: { width: 8, height: 8, borderRadius: 4 },
  simmer: { ...StyleSheet.absoluteFillObject, zIndex: 5, alignItems: 'center', justifyContent: 'center' },
  pot: {
    width: POT_SIZE, height: POT_SIZE, borderRadius: POT_SIZE / 2, padding: 6,
    backgroundColor: Paprika.cardBg,
    boxShadow: '0 16px 36px -12px rgba(82, 68, 60, 0.55)',
  },
  potInner: { flex: 1, borderRadius: (POT_SIZE - 12) / 2, overflow: 'hidden', backgroundColor: Paprika.brandTint, padding: 14 },
  potPan: { width: '100%', height: '100%' },
  chosenBadge: {
    position: 'absolute', top: 14, right: 14, width: 44, height: 44, borderRadius: 22,
    backgroundColor: Paprika.brandTint, alignItems: 'center', justifyContent: 'center',
  },
});
